import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePriestAuth } from '../context/PriestAuthContext';
import BookingsManager from '../components/priest/BookingsManager';
import MuhurtaCalculator from '../components/priest/MuhurtaCalculator';
import PatrikaGenerator from '../components/priest/PatrikaGenerator';
import InvoiceGenerator from '../components/priest/InvoiceGenerator';
import PriestProfile from '../components/priest/PriestProfile';
import './PriestDashboard.css';

const TABS = [
  { id: 'overview',  label: 'Overview',  icon: '🪔' },
  { id: 'bookings',  label: 'Bookings',  icon: '📅' },
  { id: 'muhurta',   label: 'Muhurtha',  icon: '🕉️' },
  { id: 'patrika',   label: 'Janma Patrika', icon: '📜' },
  { id: 'invoice',   label: 'Invoices',  icon: '🧾' },
  { id: 'profile',   label: 'Profile',   icon: '👤' },
];

export default function PriestDashboard() {
  const { priest, logout, priestFetch } = usePriestAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({ pending: 0, completed: 0, today: 0, total: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (!priest) navigate('/priest/login', { replace: true });
  }, [priest, navigate]);

  const loadStats = useCallback(async () => {
    try {
      const data = await priestFetch('/api/priest/bookings');
      if (data.stats) setStats(data.stats);
      if (data.bookings) setRecent(data.bookings.slice(0, 5));
    } catch (e) { /* ignore */ }
  }, [priestFetch]);

  useEffect(() => {
    if (priest) loadStats();
  }, [priest, loadStats, activeTab]);

  if (!priest) return null;

  return (
    <div className="priest-dashboard">
      {/* Sidebar */}
      <aside className="pd-sidebar">
        <div className="pd-brand">
          <span className="pd-brand-icon">🕉️</span>
          <div>
            <div className="pd-brand-title">Purohit</div>
            <div className="pd-brand-sub">Dashboard</div>
          </div>
        </div>

        <div className="pd-priest-card">
          <div className="pd-avatar">{(priest.name || 'P').charAt(0).toUpperCase()}</div>
          <div className="pd-priest-info">
            <div className="pd-priest-name">{priest.name || 'Purohit'}</div>
            <div className="pd-priest-email">{priest.email}</div>
          </div>
        </div>

        <nav className="pd-nav">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`pd-nav-item ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              <span className="pd-nav-icon">{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </nav>

        <button className="pd-logout-btn" onClick={() => { logout(); navigate('/priest/login'); }}>
          🚪 Sign Out
        </button>
      </aside>

      {/* Main */}
      <main className="pd-main">
        <header className="pd-header">
          <div>
            <h1 className="pd-title">
              {TABS.find(t => t.id === activeTab)?.icon}{' '}
              {TABS.find(t => t.id === activeTab)?.label}
            </h1>
            <p className="pd-subtitle">Manage your priestly duties with ease</p>
          </div>
        </header>

        <div className="pd-content">
          {activeTab === 'overview' && (
            <OverviewTab stats={stats} recent={recent} onJump={setActiveTab} priest={priest} />
          )}
          {activeTab === 'bookings' && <BookingsManager onChange={loadStats} />}
          {activeTab === 'muhurta'  && <MuhurtaCalculator />}
          {activeTab === 'patrika'  && <PatrikaGenerator priest={priest} />}
          {activeTab === 'invoice'  && <InvoiceGenerator priest={priest} />}
          {activeTab === 'profile'  && <PriestProfile />}
        </div>
      </main>
    </div>
  );
}

function OverviewTab({ stats, recent, onJump, priest }) {
  return (
    <div className="pd-overview">
      <div className="pd-welcome">
        <h2>🙏 Namaste, {priest.name?.split(' ')[0] || 'Swami'}</h2>
        <p>Here is your day at a glance.</p>
      </div>

      <div className="pd-stats-grid">
        <StatCard icon="📆" label="Today's Events" value={stats.today} color="saffron" />
        <StatCard icon="⏳" label="Pending"        value={stats.pending} color="gold" />
        <StatCard icon="✅" label="Completed"      value={stats.completed} color="green" />
        <StatCard icon="📊" label="Total Bookings" value={stats.total} color="purple" />
      </div>

      <div className="pd-quick-actions">
        <h3>Quick Actions</h3>
        <div className="pd-actions-grid">
          <ActionCard icon="📅" title="New Booking" desc="Schedule a pooja"
            onClick={() => onJump('bookings')} />
          <ActionCard icon="🕉️" title="Find Muhurtha" desc="Auspicious time"
            onClick={() => onJump('muhurta')} />
          <ActionCard icon="📜" title="Janma Patrika" desc="Birth chart PDF"
            onClick={() => onJump('patrika')} />
          <ActionCard icon="🧾" title="Create Invoice" desc="Bill a client"
            onClick={() => onJump('invoice')} />
        </div>
      </div>

      {recent.length > 0 && (
        <div className="pd-recent">
          <h3>Recent Bookings</h3>
          <div className="pd-recent-list">
            {recent.map(b => (
              <div key={b.id} className="pd-recent-item">
                <div>
                  <div className="pd-recent-title">{b.poojaType}</div>
                  <div className="pd-recent-sub">
                    {b.yajamaniName} • {b.eventDate}{b.eventTime ? ` at ${b.eventTime.slice(0,5)}` : ''}
                  </div>
                </div>
                <span className={`pd-status pd-status-${b.status}`}>{b.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className={`pd-stat-card pd-stat-${color}`}>
      <div className="pd-stat-icon">{icon}</div>
      <div className="pd-stat-value">{value}</div>
      <div className="pd-stat-label">{label}</div>
    </div>
  );
}

function ActionCard({ icon, title, desc, onClick }) {
  return (
    <button className="pd-action-card" onClick={onClick}>
      <div className="pd-action-icon">{icon}</div>
      <div className="pd-action-title">{title}</div>
      <div className="pd-action-desc">{desc}</div>
    </button>
  );
}
